import { Flex, Text, HelpIndicator, AgGrid } from "@jsq/ds/core";
import { Icon, IconType } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { globalEnv } from "~/global-env";
import { DocumentRefreshStatusEnum } from "~/graphql/supergraphEnums";
import { gridCellStyles } from "~/pages/Compliance/Common/GridCellBackgrounds/styles_vanilla.css";
import { mapStatusToText } from "~/pages/Compliance/ComplianceTable/utils";
import { BaseStatusTooltip } from "../BaseStatusTooltip";
import { ActionsMenu } from "./ActionsMenu";
import { DOCUMENT_STATUS_CONFIG } from "./config";
import {
  contentWrapper,
  baseContainer,
  darkIcon,
  darkText,
  statusLabelAnchor,
  cellContainer,
  launchIconContainer,
  launchIcon,
} from "./styles_vanilla.css";
import { DocumentRefreshCellRendererProps } from "./types";
import { isDocumentExpiryStatus, mapDocumentRefreshToCaseStatus } from "./utils";

export const DocumentRefreshCellRenderer = ({
  value,
  data,
  api,
}: DocumentRefreshCellRendererProps & { api?: AgGrid.GridApi }) => {
  if (data?.isEntityClientOnboardingProfile) {
    return (
      <Flex className={baseContainer} alignItems="center">
        <Text>--</Text>
      </Flex>
    );
  }

  if (isDocumentExpiryStatus(value)) {
    // No documents always means no action required — show green checkmark
    const showNoActionRequired = value === DocumentRefreshStatusEnum.NoDocuments;

    const config = showNoActionRequired
      ? DOCUMENT_STATUS_CONFIG[DocumentRefreshStatusEnum.Active]
      : DOCUMENT_STATUS_CONFIG[value];

    if (!config) {
      return null;
    }

    const textContent =
      typeof config.text.content === "object"
        ? config.text.content.render(data)
        : config.text.content;

    const iconAndText = (
      <span className={statusLabelAnchor}>
        {config.icon && (
          <Icon
            type={config.icon.type as IconType}
            variant={config.icon.variant}
            className={config.icon.className}
            data-testid={`document-refresh-status-${value}-icon`}
          />
        )}
        <Text className={config.text.className}>{textContent}</Text>
      </span>
    );

    const tooltipContent = BaseStatusTooltip({
      statusType: value,
      tooltipData: data?.documentRefreshTooltipData,
    });

    return (
      <Flex className={config.containerClass} alignItems="center">
        <div className={contentWrapper}>
          {tooltipContent ? (
            <HelpIndicator
              id={`document-refresh-status-${data?.id ?? value}-tooltip`}
              placement="top"
              content={tooltipContent}
              fullWidth
            >
              {iconAndText}
            </HelpIndicator>
          ) : (
            iconAndText
          )}
        </div>

        <ActionsMenu status={value} data={data} api={api} />
      </Flex>
    );
  }

  const mappedStatus = mapDocumentRefreshToCaseStatus(value);
  const { displayText, iconType } = mapStatusToText(mappedStatus);

  const shouldShowLaunchIcon = !!data?.documentRefreshCaseId;

  const handleLaunchIconClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (data?.documentRefreshCaseId) {
      window.open(
        `/canopy${globalEnv.complianceRoute}/case/${data.documentRefreshCaseId}`,
        "_blank",
      );
    }
  };

  return (
    <Flex className={`${baseContainer} ${gridCellStyles.default}`} alignItems="center">
      <Flex className={cellContainer} alignItems="center">
        <div className={contentWrapper}>
          {iconType && (
            <Icon
              type={iconType}
              className={darkIcon}
              data-testid={`document-refresh-status-${value}-icon`}
            />
          )}
          <Text className={darkText}>{displayText}</Text>
        </div>
        {shouldShowLaunchIcon && (
          <Flex alignItems="center" className={launchIconContainer}>
            <HelpIndicator
              id={`document-refresh-case-launch-${data?.id ?? value}-tooltip`}
              placement="top"
              content="View document refresh case"
            >
              <Icon
                type="external-link"
                variant="outline"
                color={Colors.Primary600}
                onClick={handleLaunchIconClick}
                className={launchIcon}
                data-testid="document-refresh-case-launch-icon"
              />
            </HelpIndicator>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
